<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import TasksAPI from 'dashboard/api/tasks';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  prefill: { type: Object, default: () => ({}) },
  actionItem: { type: Object, default: () => ({}) },
  conversationId: { type: Number, required: true },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const isCreating = ref(false);
const title = ref(props.prefill?.title || props.actionItem?.text || '');
const description = ref(
  props.prefill?.content || props.actionItem?.text || ''
);
const dueDate = ref(props.prefill?.due_date || '');
const assigneeId = ref('');

onMounted(() => {
  if (!agents.value?.length) {
    store.dispatch('agents/get');
  }
  if (currentUser.value?.id) {
    assigneeId.value = currentUser.value.id;
  }
});

const handleCreate = async () => {
  if (!title.value.trim()) return;
  isCreating.value = true;
  try {
    await TasksAPI.create({
      task: {
        title: title.value,
        description: description.value,
        priority: props.actionItem?.priority || 'medium',
        due_date: dueDate.value || null,
        assignee_id: assigneeId.value || null,
        taskable_type: 'Conversation',
        taskable_id: props.conversationId,
      },
    });
    useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.TASK_CREATED'));
    emit('created');
    emit('close');
  } catch {
    useAlert(t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTION_ERROR'));
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        class="absolute inset-0 bg-black/40"
        @click="emit('close')"
      />
      <div
        class="relative bg-n-background rounded-xl p-5 w-full max-w-md shadow-xl border border-n-weak mx-4"
      >
        <h3 class="text-base font-semibold text-n-slate-12 mb-4">
          {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.CREATE_TASK') }}
        </h3>

        <div class="space-y-3">
          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1 block">
              {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.TASK_TITLE') }}
            </label>
            <input
              v-model="title"
              type="text"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
            />
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1 block">
              {{
                t(
                  'CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.TASK_DESCRIPTION'
                )
              }}
            </label>
            <textarea
              v-model="description"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:border-n-brand focus:outline-none resize-none"
            />
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1 block">
              {{
                t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.TASK_DUE_DATE')
              }}
            </label>
            <input
              v-model="dueDate"
              type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
            />
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1 block">
              {{
                t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.TASK_ASSIGNEE')
              }}
            </label>
            <select
              v-model="assigneeId"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
            >
              <option value="">
                {{
                  t(
                    'CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.NO_ASSIGNEE'
                  )
                }}
              </option>
              <option
                v-for="agent in agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-5">
          <button
            class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition-colors cursor-pointer"
            @click="emit('close')"
          >
            {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.CANCEL') }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:opacity-90 rounded-lg transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isCreating || !title.trim()"
            @click="handleCreate"
          >
            <Spinner v-if="isCreating" class="size-4" />
            {{ t('CAPTAIN.BRAIN.INSIGHTS.CONVERSATION.ACTIONS.CREATE_TASK') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
